import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Layers,
  Plus,
  ArrowLeft,
  FlaskConical,
  X,
} from 'lucide-react'
import { getSoilSamples, createSoilSample, getSoilSummary, getPaddocks } from '../../services/api'
import { format, parseISO } from 'date-fns'
import StatCard from '../../components/StatCard'
import { useAuth } from '../../contexts/AuthContext'

const VEG_CLASSES = [
  { value: 'pasture_grass', label: 'Pasture Grass' },
  { value: 'lantana', label: 'Lantana' },
  { value: 'bare_soil', label: 'Bare Soil' },
  { value: 'other_shrub', label: 'Other Shrub' },
  { value: 'tree_canopy', label: 'Tree Canopy' },
]

const DEPTH_OPTIONS = [
  { value: 10, label: '0-10 cm' },
  { value: 30, label: '10-30 cm' },
]

export default function SoilSamples({ ranchId }) {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [periodFilter, setPeriodFilter] = useState('')

  const { data: samples, isLoading } = useQuery({
    queryKey: ['soilSamples', ranchId, periodFilter],
    queryFn: () => getSoilSamples(ranchId, periodFilter ? { sample_period: periodFilter } : {}),
  })

  const { data: summary } = useQuery({
    queryKey: ['soilSummary', ranchId],
    queryFn: () => getSoilSummary(ranchId),
  })

  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  const totalSamples = samples?.length || 0
  const baselineCount = summary?.filter(s => s.sample_period === 'baseline')
    .reduce((sum, s) => sum + s.sample_count, 0) || 0
  const endlineCount = summary?.filter(s => s.sample_period === 'endline')
    .reduce((sum, s) => sum + s.sample_count, 0) || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Soil Baseline Samples</h1>
            <p className="text-gray-500 mt-1">SOC and bulk density samples for carbon accounting</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Record Sample
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title="Total Samples" value={totalSamples} icon={<Layers className="h-5 w-5" />} />
        <StatCard title="Baseline Samples" value={baselineCount} icon={<FlaskConical className="h-5 w-5" />} />
        <StatCard title="Endline Samples" value={endlineCount} icon={<FlaskConical className="h-5 w-5" />} />
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['', 'baseline', 'endline'].map(period => (
          <button
            key={period}
            onClick={() => setPeriodFilter(period)}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              periodFilter === period
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {period === '' ? 'All' : period.charAt(0).toUpperCase() + period.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : totalSamples === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No soil samples recorded yet. These are critical for carbon accounting.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paddock</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Veg Class</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Depth</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SOC %</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bulk Density</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lab Ref</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {samples.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{format(parseISO(s.date_sampled), 'dd MMM yyyy')}</td>
                  <td className="px-4 py-3 text-sm font-medium">{s.paddock_name}</td>
                  <td className="px-4 py-3 text-sm">{s.vegetation_class_at_sample?.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-sm">{s.depth_cm} cm</td>
                  <td className="px-4 py-3 text-sm font-mono">
                    {s.organic_carbon_pct != null ? `${Number(s.organic_carbon_pct).toFixed(2)}%` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">
                    {s.bulk_density_g_cm3 != null ? `${Number(s.bulk_density_g_cm3).toFixed(3)}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      s.sample_period === 'baseline' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {s.sample_period}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{s.lab_reference || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <CreateSoilSampleModal
          ranchId={ranchId}
          paddocks={paddocks || []}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['soilSamples'] })
            queryClient.invalidateQueries({ queryKey: ['soilSummary'] })
            setShowModal(false)
          }}
        />
      )}
    </div>
  )
}


function CreateSoilSampleModal({ ranchId, paddocks, onClose, onSuccess }) {
  const { userId } = useAuth()
  const [form, setForm] = useState({
    paddock_id: '',
    vegetation_class_at_sample: 'pasture_grass',
    date_sampled: new Date().toISOString().split('T')[0],
    depth_cm: 10,
    bulk_density_g_cm3: '',
    organic_carbon_pct: '',
    sample_period: 'baseline',
    gps_lat: '',
    gps_lon: '',
    lab_reference: '',
    notes: '',
  })
  const [error, setError] = useState(null)

  const mutation = useMutation({
    mutationFn: createSoilSample,
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to create sample'),
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.paddock_id || !form.gps_lat || !form.gps_lon) {
      setError('Paddock and GPS coordinates are required')
      return
    }
    mutation.mutate({
      ranch_id: ranchId,
      paddock_id: form.paddock_id,
      vegetation_class_at_sample: form.vegetation_class_at_sample,
      date_sampled: form.date_sampled,
      depth_cm: parseFloat(form.depth_cm),
      bulk_density_g_cm3: form.bulk_density_g_cm3 ? parseFloat(form.bulk_density_g_cm3) : null,
      organic_carbon_pct: form.organic_carbon_pct ? parseFloat(form.organic_carbon_pct) : null,
      sample_period: form.sample_period,
      gps_lat: parseFloat(form.gps_lat),
      gps_lon: parseFloat(form.gps_lon),
      lab_reference: form.lab_reference || null,
      collected_by: userId,
      notes: form.notes || null,
    })
  }

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-green-600" />
            Record Soil Sample
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Paddock *</label>
              <select className="input" value={form.paddock_id} onChange={e => update('paddock_id', e.target.value)}>
                <option value="">Select...</option>
                {paddocks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Date Sampled *</label>
              <input type="date" className="input" value={form.date_sampled} onChange={e => update('date_sampled', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Vegetation Class *</label>
              <select className="input" value={form.vegetation_class_at_sample} onChange={e => update('vegetation_class_at_sample', e.target.value)}>
                {VEG_CLASSES.map(vc => <option key={vc.value} value={vc.value}>{vc.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Depth *</label>
              <select className="input" value={form.depth_cm} onChange={e => update('depth_cm', e.target.value)}>
                {DEPTH_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Sample Period *</label>
              <select className="input" value={form.sample_period} onChange={e => update('sample_period', e.target.value)}>
                <option value="baseline">Baseline</option>
                <option value="endline">Endline</option>
              </select>
            </div>
            <div>
              <label className="label">Lab Reference</label>
              <input type="text" className="input" placeholder="Lab ID..." value={form.lab_reference} onChange={e => update('lab_reference', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Organic Carbon (%)</label>
              <input type="number" step="0.01" className="input" placeholder="e.g. 1.45" value={form.organic_carbon_pct} onChange={e => update('organic_carbon_pct', e.target.value)} />
            </div>
            <div>
              <label className="label">Bulk Density (g/cm3)</label>
              <input type="number" step="0.001" className="input" placeholder="e.g. 1.350" value={form.bulk_density_g_cm3} onChange={e => update('bulk_density_g_cm3', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">GPS Latitude *</label>
              <input type="number" step="0.000001" className="input" placeholder="-0.xxxxx" value={form.gps_lat} onChange={e => update('gps_lat', e.target.value)} />
            </div>
            <div>
              <label className="label">GPS Longitude *</label>
              <input type="number" step="0.000001" className="input" placeholder="30.xxxxx" value={form.gps_lon} onChange={e => update('gps_lon', e.target.value)} />
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea className="input" rows={2} placeholder="Soil type, conditions..." value={form.notes} onChange={e => update('notes', e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">Cancel</button>
            <button type="submit" disabled={mutation.isPending} className="btn bg-green-600 text-white hover:bg-green-700">
              {mutation.isPending ? 'Saving...' : 'Save Sample'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
